import React from 'react';
import { ArrowLeft } from 'lucide-react';
import ScannerTable from './ScannerTable';
import CoinDetail from './CoinDetail';
import PositionAnalyzer from './PositionAnalyzer';
import DecisionReport from './DecisionReport';
import AlertsPanel from './AlertsPanel';
import LegendCard from './LegendCard';
import CoinSearch from './CoinSearch';
import HighProbabilityStrip from './HighProbabilityStrip';
import RadarStrip from './RadarStrip';
import RadarHistoryPanel from './RadarHistoryPanel';
import FavoritesLivePanel from './FavoritesLivePanel';
import AICommentaryCard from './AICommentaryCard';
import SystemStatsCard from './SystemStatsCard';
import ActiveRecommendations from './ActiveRecommendations';
import MultiTimeframeStrip from './MultiTimeframeStrip';
import SectionHeader from './SectionHeader';
import TelegramLinkCard from './TelegramLinkCard';
import MyTrackingPanel from './MyTrackingPanel';
import DataHealthBadge from './DataHealthBadge';
import RegimeBadge from './RegimeBadge';
import PaperLedgerCard from './PaperLedgerCard';
import ImpulseStrip from './ImpulseStrip';
import TrainReportCard from './TrainReportCard';
import CandidatesPanel from './CandidatesPanel';
import RadarPrepToggle from './RadarPrepToggle';
import AutoTradePanel from './AutoTradePanel';
import ScalperPanel from './ScalperPanel';
import RangeBotPanel from './RangeBotPanel';
import OtherCoinsTable from './OtherCoinsTable';
import HomeOverview from './HomeOverview';
import LearningPage from './LearningPage';
import EarningsPage from './EarningsPage';
import { MobileDisclosure } from './MobileParts';

/** Masaüstü sırası: sayfa tek akış — sade görünüm: 2 blok (Adaylar + Diğer coinler), gerisi "Detay" altında katlı. */
export const DESKTOP_ORDER = [
    'regime', 'adaylar', 'digerCoinler', 'bot', 'coin', 'takibim', 'detay', 'radarGecmis',
    'basari', 'favoriler', 'telegram', 'tarayici', 'legend', 'uyarilar',
];

/** Mobil sekmeler: her sekme sadece kendi bölümlerini gösterir (kaydırma yok). */
export const MOBILE_TABS = {
    sinyaller: ['regime', 'adaylar', 'digerCoinler', 'detay'],
    takip: ['bot', 'takibim'],
    coin: ['coin'],
    favoriler: ['favoriler'],
};

export const MORE_ITEMS = [
    { key: 'telegram', label: '📱 Telegram' },
    { key: 'basari', label: '🏆 Başarı' },
    { key: 'tarayici', label: '📊 Tarayıcı' },
    { key: 'uyarilar', label: '🚨 Uyarılar' },
    { key: 'radarGecmis', label: '📜 Radar Geçmişi' },
    { key: 'ogrenme', label: '🧠 Öğrenme' },
    { key: 'kazanc', label: '💰 Kazanç' },
    { key: 'legend', label: '📖 Rehber' },
];

const SECTIONS = {
    coin: (c) => (
        <React.Fragment key="coin">
            <div className="flex items-center justify-between gap-3 flex-wrap bg-slate-800 border border-slate-700 rounded-xl px-4 py-3">
                <div className="min-w-0 flex items-center gap-3">
                    {c.backTab && (
                        <button
                            data-testid="mobile-back-button"
                            onClick={c.goBack}
                            className="shrink-0 inline-flex items-center gap-1 rounded-full border border-slate-600 bg-slate-700/60 px-2.5 py-1 text-[11px] text-slate-200"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> Geri
                        </button>
                    )}
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400">Aktif Coin</div>
                        <div className="text-xl sm:text-2xl font-mono font-medium tracking-tight text-slate-50 mt-1 truncate" data-testid="active-symbol">
                            {c.selected ? c.selected.replace('USDT', '') : '—'}
                            <span className="text-slate-400 text-xs sm:text-sm ml-1">/USDT</span>
                        </div>
                    </div>
                </div>
                {!c.isMobile && <DataHealthBadge rows={c.rows} streamStatus={c.streamStatus} />}
                <CoinSearch onSelect={c.handleSearchSelect} />
            </div>

            <SectionHeader
                id="pozisyon"
                emoji="💼"
                title="Pozisyon Analizi — Seçili Coin"
                subtitle="Elindeki parayı yaz, sistem sana giriş/hedef/stop ve kaldıraç tavsiyesi versin. Listelerden coin seçince burada güncellenir."
            />
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
                <div className="lg:col-span-6 min-h-[420px] sm:min-h-[520px]">
                    <CoinDetail symbol={c.selected} timeframe={c.timeframe} tick={c.tickPrices?.[c.selected]} listVerdict={c.verdictBySymbol?.[c.selected]} />
                </div>
                <div className="lg:col-span-3 min-h-[420px] sm:min-h-[520px]">
                    <PositionAnalyzer symbol={c.selected} listVerdict={c.verdictBySymbol?.[c.selected]} />
                </div>
                <div className="lg:col-span-3 sm:min-h-[520px]">
                    <MobileDisclosure isMobile={c.isMobile} title="📝 Karar Raporu" hint="Türkçe detaylı analiz — aç/kapat" testId="mobile-report-disclosure">
                        <DecisionReport symbol={c.selected} timeframe={c.timeframe} />
                    </MobileDisclosure>
                </div>
            </div>
            {c.selected && (
                <MobileDisclosure isMobile={c.isMobile} title="🤖 AI Yorumu" hint="Seçili coin için yapay zekâ yorumu — aç/kapat" testId="mobile-ai-disclosure">
                    <AICommentaryCard symbol={c.selected} />
                </MobileDisclosure>
            )}
        </React.Fragment>
    ),

    bot: (c) => (
        <React.Fragment key="bot">
            {SECTIONS.autobot(c)}
            {SECTIONS.scalper(c)}
        </React.Fragment>
    ),

    autobot: (c) => (
        <React.Fragment key="autobot">
            <SectionHeader
                id="bot"
                emoji="🤖"
                title="Otomatik Bot — kararları kendi açar ve yönetir"
                subtitle="🎯 AÇ ve 🚀 KÜÇÜK AÇ'ta hemen açar; 🔭 EMİR KOY'da koşullu emri Binance'e koyar ve tetiği izler, hacimle kırılınca açar; 🔥 ETİKET (patlama etiketi yüksek, %50+) da tam slotla açar. Her pozisyonda borsada stop + Hedef 1 durur; Hedef 1'de %30 kapanır, kalan Hedef 2 + iz süren stop ile koşar. Kaldıraç, hedef oranı, zaman kuralı, ekleme ve öğrenme ayarlardan yönetilir. Elle açtığın işlemlere dokunmaz."
            />
            <AutoTradePanel onSelect={c.selectCoin} verdicts={c.verdictBySymbol} at={c.at} />
        </React.Fragment>
    ),

    scalper: (c) => (
        <React.Fragment key="scalper">
            <SectionHeader
                id="hizli"
                emoji="⚡"
                title="Hızlı Kâr Botu — ayrı bütçe, tek slot, küçük kârı hemen al"
                subtitle="En oynak coinlere (ya da sinyallere) 3–5x girer; komisyon sonrası net +%2 ROI'de kârı alır, −%25 ROI'de stop olur; 2 saatte sıfıra yakınsa çıkar; stop olan coine o gün girmez; günde en fazla 8 stop. Aynı coine art arda girebilir. Elle açtığın ve ana botun pozisyonlarına dokunmaz."
            />
            <ScalperPanel onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    range: (c) => (
        <React.Fragment key="range">
            <SectionHeader
                id="aralik"
                emoji="📐"
                title="Aralık Botu — seçtiğin coinlerde, yazdığın fiyat aralığında kademe kademe al-sat"
                subtitle="Coini, alt–üst fiyatı, kademe sayısını, teminatı ve kaldıracı sen yazarsın (örn. ARB 0,165–0,170, 4 kademe, 350$ × 12x). Fiyat bir kademeye inince alır, bir üst kademede satar; satınca fiyat yeniden kademeye inince tekrar alır. Aralığın altına kırılırsa satmaz, bekler — yalnız sigorta stopu (ROI −%50) likidasyonu önler. Elle ve diğer botların pozisyonuna dokunmaz."
            />
            <RangeBotPanel onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    home: (c) => <HomeOverview key="home" ctx={c} />,

    onerilerim: (c) => (
        <React.Fragment key="onerilerim">
            <SectionHeader
                id="onerilerim"
                emoji="📋"
                title="Sistem Önerilerim — Takip Paneli"
                subtitle="Sadece RESMİ sinyaller (güven ≥%80, R/R ≥2). Her öneri TP, stop, yön değişimi veya 24 saat dolana kadar burada kalır. Pozisyon açtıysan 'Takibe Al'a bas."
            />
            <ActiveRecommendations onSelect={c.selectCoin} tickPrices={c.tickPrices} />
        </React.Fragment>
    ),

    takibim: (c) => (
        <React.Fragment key="takibim">
            <SectionHeader
                id="takibim"
                emoji="📌"
                title="Benim Takibim"
                subtitle="Takibe aldığın öneriler (web'den 'Takibe Al' veya Telegram'da /aldim). Canlı kâr/zarar, sistemin şu anki tavsiyesi ve 'Kapattım' butonu burada."
            />
            <MyTrackingPanel onSelect={c.selectCoin} tickPrices={c.tickPrices} />
        </React.Fragment>
    ),

    guclu: (c) => (
        <React.Fragment key="guclu">
            <SectionHeader
                id="guclu-alarm"
                emoji="🚨"
                title="GÜÇLÜ ALARM — Çoklu Zaman Dilimi"
                subtitle="Hem kısa (15 dk) hem orta (4 saat) grafikten aynı yönde onay alan coinler. En güvenilir sinyaller — kısa vadeli gürültüyü eler."
            />
            <MultiTimeframeStrip rows={c.liveRows} onSelect={c.selectCoin} tickPrices={c.tickPrices} />
        </React.Fragment>
    ),

    regime: (c) => <RegimeBadge key="regime" regime={c.regime} />,

    adaylar: (c) => (
        <React.Fragment key="adaylar">
            <SectionHeader
                id="adaylar"
                emoji="🔥"
                title="Bugün ne yapmalı — Patlama Adayları + Sistem Önerileri, botun durumuyla"
                subtitle="Tek liste, tek karar: Yön (▲ YUKARI / ▼ AŞAĞI) · Patlama ihtimali (% — geçmiş veriden) · Ne yapmalı (AÇ · KÜÇÜK AÇ · EMİR KOY · BEKLE). Her coinin yanında 🤖 botun ne yaptığı yazar: açtı / tetik bekliyor / açmadı: sebep. Sen elle bir şey açmak zorunda değilsin."
            />
            <CandidatesPanel rows={c.liveRows} onSelect={c.selectCoin} tickPrices={c.tickPrices} botStatus={c.at?.status} />
            <RadarPrepToggle />
        </React.Fragment>
    ),

    digerCoinler: (c) => (
        <React.Fragment key="digerCoinler">
            <SectionHeader
                id="diger-coinler"
                emoji="🧭"
                title="Diğer Coinler — Hangi Yöne Eğilimli?"
                subtitle="Patlama adayı olmayan coinler: yön eğilimi ve güveni. Karar hepsinde BEKLE; koşmuş olanlarda KOVALAMA YAPMA."
            />
            <OtherCoinsTable rows={c.liveRows} onSelect={c.selectCoin} tickPrices={c.tickPrices} />
        </React.Fragment>
    ),

    detay: (c) => (
        <MobileDisclosure
            key="detay"
            isMobile={c.isMobile}
            force
            title="🔎 Detaylı analiz — kararın arkası"
            hint="Sistem Önerileri geçmişi (#ID, sonuçlar) · Yüksek İhtimal · İlk Hareket · Radar (7 teyit, planlar) · Tren Raporu · Güçlü Alarm. Yukarıdaki kararlar bu şeritlerden üretilir."
            testId="detail-disclosure"
        >
            {['onerilerim', 'firsatlar', 'ilkHareket', 'radar', 'trenRaporu', 'guclu'].map((k) => SECTIONS[k](c))}
        </MobileDisclosure>
    ),

    firsatlar: (c) => (
        <React.Fragment key="firsatlar">
            <SectionHeader
                id="firsatlar"
                emoji="🎯"
                title="Yüksek İhtimal Fırsatları — ŞİMDİ İşlem Açılabilir"
                subtitle='Sadece RESMİ sinyaller (güven ≥%80, R/R ≥2, risk makul). Giriş, hedef ve stop belli; her kart "ne kadar kazanırsın / ne kadar kaybedersin" gösterir.'
            />
            <HighProbabilityStrip rows={c.liveRows} onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    ilkHareket: (c) => (
        <React.Fragment key="ilkHareket">
            <SectionHeader
                id="ilk-hareket"
                emoji="🚀"
                title="İlk Hareket — Tren İstasyonda"
                subtitle="Hareketin BAŞLANGICI: hacim x2+, 1 saatte %2-5 ivme, açık pozisyon artıyor, 24 saatlik değişim hâlâ %8 altı. Küçük pozisyon planı (giriş/stop/TP/kaldıraç) ile Telegram'a da gider. Resmi sinyal değildir."
            />
            <ImpulseStrip rows={c.liveRows} onSelect={c.selectCoin} tickPrices={c.tickPrices} />
        </React.Fragment>
    ),

    radar: (c) => (
        <React.Fragment key="radar">
            <SectionHeader
                id="erken-kesif"
                emoji="🔭"
                title="Erken Keşif Radarı — 7 Teyit Sistemi"
                subtitle="Her coin 7 maddelik kontrolden geçer. İZLE (1-2 teyit) → HAZIRLAN (3+, henüz açma) → POZİSYON AÇ (en az 4/7 + kırılım teyidi + resmi sinyal). Fiyat zaten koşmuşsa (24s ≥%10, 2×ATR ötesi, RSI ≥75) kart TREN KAÇTI olur: HAZIRLAN sayılmaz, sadece geri çekilme planı. Kaliteli ERKEN HAZIRLAN'lar Telegram'a herkese gider."
            />
            <RadarStrip rows={c.liveRows} onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    trenRaporu: (c) => <TrainReportCard key="trenRaporu" compact={c.isMobile} />,

    radarGecmis: () => (
        <React.Fragment key="radarGecmis">
            <SectionHeader
                id="radar-gecmis"
                emoji="📜"
                title="Radar Sonuç Geçmişi — Hangi Kurulum Tutuyor?"
                subtitle="Radarın geçmiş HAZIRLAN / POZİSYON AÇ sinyalleri: coin bazında kaçı tuttu, en iyi hareket, önerilen kaldıraçla kâr/zarar ve hangi teyitlerin daha çok tuttuğu."
            />
            <RadarHistoryPanel />
        </React.Fragment>
    ),

    ogrenme: (c) => (
        <React.Fragment key="ogrenme">
            <SectionHeader
                id="ogrenme"
                emoji="🧠"
                title="Öğrenme & Gelişim — bot ne öğrendi, hangi seviyede, sırada ne var?"
                subtitle="Bot kapanan işlemlerinden ders çıkarır: öğrenme seviyesi veriyle yükselir, trader notu kâr faktörüne göre verilir. Zararlı karar türünü 48 saat durdurur, art arda 3 zarar eden coini 24 saat atlar; boyutuna dokunmaz. Her kural değişikliği geçmişte görünür."
            />
            <LearningPage onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    kazanc: (c) => (
        <React.Fragment key="kazanc">
            <SectionHeader
                id="kazanc"
                emoji="💰"
                title="Kazanç Raporu — günlük ve aylık sonuçlar"
                subtitle="Normal bot, Hızlı Kâr Botu ve Aralık Botu için gerçek / sanal defter, Bugün · Dün · 7 gün · 30 gün · Bu ay · Geçen ay · Tümü ya da tarih aralığı. Net = komisyon düşülmüş gerçek sonuç; 'hedef' diye kapanıp zarar yazan işlemler zarar sayılır."
            />
            <EarningsPage onSelect={c.selectCoin} />
        </React.Fragment>
    ),

    basari: (c) => (
        <React.Fragment key="basari">
            <SectionHeader
                id="basari"
                emoji="🏆"
                title="Sistem Başarı Oranı"
                subtitle="Sadece resmi sinyaller sayılır. Her işlem gerçek plana göre ölçülür (TP1'de yarısını kapat, stop'u girişe çek); yön değişenler ve süresi dolanlar da o anki kâr/zararıyla dahil."
            />
            <SystemStatsCard binanceOnly={c.onlyBinance} />
            <PaperLedgerCard />
        </React.Fragment>
    ),

    favoriler: (c) => (
        <React.Fragment key="favoriler">
            <SectionHeader
                id="favoriler"
                emoji="⭐"
                title="Favorilerim — Canlı Takip"
                subtitle="⭐ ile işaretlediğin coinler burada 10 saniyede bir güncellenir. Hedef veya stop'a değince uyarı gelir."
            />
            <FavoritesLivePanel
                favorites={c.streamFavs?.list || []}
                levelEvents={c.streamFavs?.events || []}
                lastUpdate={c.streamFavs?.ts}
                watchlist={c.watchlist}
                onSelect={c.selectCoin}
                tickPrices={c.tickPrices}
            />
        </React.Fragment>
    ),

    telegram: () => (
        <React.Fragment key="telegram">
            <SectionHeader
                id="telegram"
                emoji="📱"
                title="Telegram'a Bildirim Gönder"
                subtitle="Dashboard'u açık tutmana gerek kalmaz — resmi sinyaller (#ID, LONG/SHORT, kaldıraç), HAZIRLAN planları ve 'veri bayat' uyarıları telefonuna anında ulaşır."
            />
            <TelegramLinkCard />
        </React.Fragment>
    ),

    tarayici: (c) => (
        <React.Fragment key="tarayici">
            <SectionHeader
                id="tarayici"
                emoji="📊"
                title="Tüm Fırsatlar — Tarayıcı"
                subtitle="Tüm coinler, puanları ve tavsiyeleri. İstersen sadece favorilerini veya Binance Futures coinlerini filtrele."
            />
            <ScannerTable
                rows={c.liveRows}
                loading={c.loading}
                onSelect={c.selectCoin}
                selectedSymbol={c.selected}
                onToggleFavorite={c.toggleFavorite}
                onlyFavorites={c.onlyFavorites}
                onOnlyFavoritesChange={c.setOnlyFavorites}
                onlyBinance={c.onlyBinance}
                onOnlyBinanceChange={c.setOnlyBinance}
                tickPrices={c.tickPrices}
                openPositionSymbols={c.openPositionSymbols}
            />
        </React.Fragment>
    ),

    legend: () => <LegendCard key="legend" />,

    uyarilar: (c) => (
        <React.Fragment key="uyarilar">
            <SectionHeader
                id="uyarilar"
                emoji="🚨"
                title="Erken Uyarılar"
                subtitle="Olağandışı hacim, pump/dump sinyali veya haber çıkınca burada görünür."
            />
            <div className="min-h-[260px]">
                <AlertsPanel alerts={c.alerts} onSelect={c.selectCoin} verdicts={c.verdictBySymbol} />
            </div>
        </React.Fragment>
    ),
};

export default function DashboardSections({ order, ctx }) {
    return <>{order.map((key) => SECTIONS[key]?.(ctx))}</>;
}
